<template>
  <section :aria-busy="loading" :aria-label="ariaLabel">
    <slot v-if="loading" name="loading-content">
      <ProductCardSkeleton
        v-for="index in perPage"
        :key="`product-loading-${index}`"
      />
    </slot>

    <template v-else>
      <slot
        v-for="(product, index) in products"
        name="product"
        :product="product"
        :index="index"
        :loading="loading"
      >
        <AgsLazy
          :key="`product-lazy-${product.id}`"
          placeholder-ratio="7/13"
          :important="index < priorityCount"
          :ssr="isCrawler"
        >
          <ProductCard
            :key="`product-${product.id}`"
            data-test-id="product-item"
            :product="product"
            :siblings="getSiblingsColors(product) as ProductSibling[]"
            :colors="getProductAndSiblingsColors(product, 'colorRange')"
            :link="getProductDetailRoute(product)"
            :image="getFrontImage(product.images)"
            :hover-image="getHoverImage(product.images)"
            :loading="loading"
            :price="getLowestPriceBetweenVariants(product)"
            :show-price-reduction="isActiveSalePeriod"
            :list-type="listType"
            :list-name="listName"
            :list-index="index"
            :selected-category="selectedCategory"
            @click:product="$emit('click:product', product, index)"
            @intersect:product="collectIntersection(product, index)"
          >
            <template #header-badge>
              <ProductCardBadges :product="product" />
            </template>
          </ProductCard>
        </AgsLazy>
      </slot>
    </template>

    <slot v-if="!loading && !products?.length" name="no-products-found">
      <p
        class="col-span-full mt-8 text-center text-xl font-medium leading-tight"
      >
        {{ $t('plp.no_products_found') }}
      </p>
    </slot>

    <slot v-else name="listing" />
  </section>
</template>

<script setup lang="ts">
import type { Product, ProductSibling, Category } from '@scayle/storefront-nuxt'
import { getProductAndSiblingsColors } from '@scayle/storefront-nuxt'
import type { ListedProduct, ListItemType } from '~/composables/tracking/types'
import useProductIntersectionCollection from '#layers/base/composables/useProductIntersectionCollection'

const props = withDefaults(
  defineProps<{
    products?: Product[]
    loading?: boolean
    perPage?: number
    selectedCategory?: Category
    listType?: ListItemType
    listName?: string
    priorityProductCount?: number
    ariaLabel?: string
  }>(),
  {
    products: () => [],
    perPage: 24,
    selectedCategory: undefined,
    listType: undefined,
    listName: '',
    priorityProductCount: undefined,
    ariaLabel: undefined,
  },
)

const emit = defineEmits<{
  'intersect:products': [items: ListedProduct[]]
  'click:product': [value: Product, index: number]
}>()

const route = useRoute()
const { isMobile } = useDeviceType()
const isCrawler = useNuxtApp().$device.isCrawler

const { isActiveSalePeriod } = useSmartShopConfiguration()

const currentPage = computed(() => parseInt(route.query.page as string) || 1)
const priorityCount = computed(
  () => props.priorityProductCount ?? (isMobile.value ? 4 : 8),
)

const { collectIntersection } = useProductIntersectionCollection({
  perPage: props.perPage,
  currentPage,
  onIntersection: (items) => emit('intersect:products', items),
})
</script>
